/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import { useCallback, useEffect, useRef } from "react"

import type {
  ActivityMessage,
  ChatAppResponse,
  DocumentReferencePreview,
  PendingFile,
  ToolApproval,
} from "@/api/models"

import { safeImport } from "@/lib/chunk-reload"
import { logger } from "@/lib/logger"

import { streamOwnsStreamingSlot } from "./stream-turn-ownership"
import { mergeActivitiesPreservingClientInjected } from "../utils/merge-client-injected-activities"

const CONTENT_RENDER_CADENCE_MS = 30
const CONTROL_DEBOUNCE_MS = 140
const FINALIZE_SETTLE_MS = 80

// StreamEvent represents what we receive directly from the web socket client
// This is the data object from ChunkMessage, not the full ChunkMessage
export type StreamEvent = {
  content?: string
  context?: unknown
  tool_calls?: unknown[]
  finish_reason?: string
  response_complete?: boolean
  error?: string
  _activities?: ActivityMessage[]
}

type ChatState = {
  messages: [string, ChatAppResponse][]
  streamingAnswer: [string, ChatAppResponse] | null
  isLoading: boolean
  isStreaming: boolean
  isAwaitingSave: boolean
  canContinue: boolean
  error?: unknown
  toolApprovals?: ToolApproval[]
  toolApprovalSessionId?: string | null
}

type PendingUpdateTarget = Pick<StreamContext, "sessionId" | "getChatState" | "currentResponse">

export type StreamContext = {
  streamedContent: string
  question: string
  currentResponse: ChatAppResponse
  signal: AbortSignal
  isContinuation: boolean
  sessionId: string | null
  writeChatState: (state: Partial<ChatState>) => void
  getChatState: () => {
    messages: [string, ChatAppResponse][]
    streamingAnswer: [string, ChatAppResponse] | null
  }
}

export type StreamRequestOptions = {
  question: string
  responseBody: ReadableStream<Uint8Array>
  signal: AbortSignal
  isContinuation?: boolean
  writeChatState: StreamContext["writeChatState"]
  initialResponse?: ChatAppResponse
  sessionId: string | null
  getChatState: () => {
    messages: [string, ChatAppResponse][]
    streamingAnswer: [string, ChatAppResponse] | null
  }
}

function buildToolApprovalSignature(approvals?: ToolApproval[]): string {
  if (!approvals || approvals.length === 0) {
    return ""
  }

  return approvals
    .map(
      (approval) =>
        `${approval.request_id}:${approval.status}:${approval.updated_at ?? ""}:${approval.call_id}`
    )
    .sort()
    .join("|")
}

function normalizeDocumentReferences(raw: unknown): DocumentReferencePreview[] {
  if (!Array.isArray(raw)) {
    return []
  }

  return raw.filter((item): item is DocumentReferencePreview => {
    return (
      !!item &&
      typeof item === "object" &&
      "document_id" in item &&
      "title" in item &&
      "snippet" in item
    )
  })
}

function mergeDocumentReferences(
  current: DocumentReferencePreview[] | undefined,
  incoming: unknown
): DocumentReferencePreview[] | undefined {
  const normalizedIncoming = normalizeDocumentReferences(incoming)
  if (normalizedIncoming.length === 0) {
    return current
  }

  const merged = [...(current ?? []), ...normalizedIncoming]
  const deduped = new Map<string, DocumentReferencePreview>()

  merged.forEach((reference) => {
    const key = `${reference.document_id}:${reference.version ?? "latest"}:${reference.change_type ?? "updated"}`
    deduped.set(key, reference)
  })

  return Array.from(deduped.values())
}

export function useStreamHandler() {
  const controlTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const settleTimeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const writeStateIdsRef = useRef(new WeakMap<StreamContext["writeChatState"], number>())
  const writeStateIdCounterRef = useRef(0)
  const pendingStateRef = useRef<
    Map<
      string,
      {
        writeState: StreamContext["writeChatState"]
        state: Partial<ChatState>
        streamTurnId: string | undefined
        /**
         * A queued update can be overtaken by `message_saved`, which commits
         * the canonical row and clears `streamingAnswer` before this debounce
         * fires. Keep a live-state reader so the flush can refuse to revive a
         * terminal (or newer) stream slot.
         */
        getChatState: StreamContext["getChatState"]
      }
    >
  >(new Map())

  const getPendingKey = useCallback(
    (
      writeState: StreamContext["writeChatState"],
      sessionId: string | null,
      streamTurnId?: string
    ) => {
      const writeStateIds = writeStateIdsRef.current
      let id = writeStateIds.get(writeState)

      if (id == null) {
        id = writeStateIdCounterRef.current++
        writeStateIds.set(writeState, id)
      }

      return `${id}:${sessionId ?? "null"}:${streamTurnId ?? "legacy"}`
    },
    []
  )

  const clearPendingStateForStream = useCallback(
    (
      writeState: StreamContext["writeChatState"],
      sessionId: string | null,
      streamTurnId?: string
    ) => {
      const key = getPendingKey(writeState, sessionId, streamTurnId)
      pendingStateRef.current.delete(key)

      if (pendingStateRef.current.size === 0) {
        if (controlTimeoutRef.current != null) {
          clearTimeout(controlTimeoutRef.current)
          controlTimeoutRef.current = null
        }
        if (contentTimeoutRef.current != null) {
          clearTimeout(contentTimeoutRef.current)
          contentTimeoutRef.current = null
        }
      }

      return key
    },
    [getPendingKey]
  )

  const runPendingUpdates = useCallback(() => {
    if (controlTimeoutRef.current != null) {
      clearTimeout(controlTimeoutRef.current)
      controlTimeoutRef.current = null
    }
    if (contentTimeoutRef.current != null) {
      clearTimeout(contentTimeoutRef.current)
      contentTimeoutRef.current = null
    }

    const updates = Array.from(pendingStateRef.current.values())
    pendingStateRef.current.clear()

    for (const update of updates) {
      try {
        const pendingStreaming = update.state.streamingAnswer
        const currentStreaming = update.getChatState().streamingAnswer
        const updateTurnId = update.streamTurnId ?? pendingStreaming?.[1].turn_id
        const stillOwnsStreamingSlot =
          currentStreaming != null
            ? streamOwnsStreamingSlot(updateTurnId, currentStreaming[1].turn_id)
            : updateTurnId == null && !pendingStreaming

        if (!stillOwnsStreamingSlot) {
          // A terminal save (or a newer turn) already owns the UI. Every
          // coalesced field belongs to the old stream: applying flags such as
          // `isStreaming: false` after dropping only `streamingAnswer` would
          // incorrectly stop the newer turn. Discard the whole stale update.
          continue
        }

        update.writeState(update.state)
      } catch (error) {
        logger.error("Error running pending state update", error as Error)
      }
    }
  }, [])

  const flushPendingState = useCallback(() => {
    if (controlTimeoutRef.current != null) {
      clearTimeout(controlTimeoutRef.current)
      controlTimeoutRef.current = null
    }
    if (contentTimeoutRef.current != null) {
      clearTimeout(contentTimeoutRef.current)
      contentTimeoutRef.current = null
    }

    runPendingUpdates()
  }, [runPendingUpdates])

  const scheduleStateUpdate = useCallback(
    (
      writeState: StreamContext["writeChatState"],
      state: Partial<ChatState>,
      target: PendingUpdateTarget
    ) => {
      const streamTurnId = target.currentResponse.turn_id
      const key = getPendingKey(writeState, target.sessionId, streamTurnId)
      const currentState = pendingStateRef.current.get(key)
      const nextState = currentState ? { ...currentState.state, ...state } : { ...state }

      pendingStateRef.current.set(key, {
        writeState,
        state: nextState,
        streamTurnId,
        getChatState: target.getChatState,
      })

      if (controlTimeoutRef.current == null) {
        controlTimeoutRef.current = setTimeout(() => {
          controlTimeoutRef.current = null
          runPendingUpdates()
        }, CONTROL_DEBOUNCE_MS)
      }
    },
    [getPendingKey, runPendingUpdates]
  )

  const scheduleContentUpdate = useCallback(
    (
      writeState: StreamContext["writeChatState"],
      state: Partial<ChatState>,
      target: PendingUpdateTarget
    ) => {
      const streamTurnId = target.currentResponse.turn_id
      const key = getPendingKey(writeState, target.sessionId, streamTurnId)
      const currentState = pendingStateRef.current.get(key)
      const nextState = currentState ? { ...currentState.state, ...state } : { ...state }

      pendingStateRef.current.set(key, {
        writeState,
        state: nextState,
        streamTurnId,
        getChatState: target.getChatState,
      })

      if (contentTimeoutRef.current == null) {
        contentTimeoutRef.current = setTimeout(() => {
          contentTimeoutRef.current = null
          runPendingUpdates()
        }, CONTENT_RENDER_CADENCE_MS)
      }
    },
    [getPendingKey, runPendingUpdates]
  )

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        flushPendingState()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      flushPendingState()
      if (controlTimeoutRef.current != null) {
        clearTimeout(controlTimeoutRef.current)
        controlTimeoutRef.current = null
      }
      if (contentTimeoutRef.current != null) {
        clearTimeout(contentTimeoutRef.current)
        contentTimeoutRef.current = null
      }
      // Settles are bounded origin-session store work, not render work. Let each
      // armed callback survive route unmount and remove itself after it runs.
    }
  }, [flushPendingState])

  const updateStreamedAnswers = useCallback(
    (streamContext: StreamContext, latestResponse: ChatAppResponse) => {
      const { question, getChatState } = streamContext

      // Always read from live store to get latest activities
      const chatState = getChatState()
      const currentStreaming = chatState.streamingAnswer

      if (!currentStreaming) {
        logger.warn("updateStreamedAnswers: no streaming answer exists")
        return
      }

      const [existingQuestion, currentAnswer] = currentStreaming

      // Drop stale writes once a newer turn owns the streaming slot (queue drain /
      // fast follow-up). Mirrors settle ownership below.
      if (!streamOwnsStreamingSlot(streamContext.currentResponse.turn_id, currentAnswer.turn_id)) {
        return
      }

      // Preserve activities that WebSocket may have added.
      // Also preserve `thinking` that may have been accumulated out-of-band
      // (e.g. Deep Explorer pre-approval reasoning via deep_planning_chunk).
      // The streamed `latestResponse` is built from the regular content
      // channel and doesn't carry `thinking`, so without this merge the
      // first post-approval content chunk would wipe the Thinking panel.
      const mergedResponse: ChatAppResponse = {
        ...latestResponse,
        activities: currentAnswer.activities || latestResponse.activities || [],
        thinking: currentAnswer.thinking || latestResponse.thinking,
      }

      // Update streamingAnswer with new content and preserved activities
      scheduleContentUpdate(
        streamContext.writeChatState,
        {
          streamingAnswer: [existingQuestion ?? question, mergedResponse],
        },
        streamContext
      )
    },
    [scheduleContentUpdate]
  )

  // Push an authoritative activities snapshot (from the WS client's
  // `_activities` event) onto the in-progress streaming answer and schedule an
  // immediate render. The snapshot is truth for *server* activities, but must
  // preserve client-injected rows (steer_applied from #2469) that the backend
  // never echoes — a naive full replace was wiping them from Activity History.
  const updateStreamedActivities = useCallback(
    (streamContext: StreamContext, activities: ActivityMessage[]) => {
      const chatState = streamContext.getChatState()
      const currentStreaming = chatState.streamingAnswer
      if (!currentStreaming) return

      const [existingQuestion, currentAnswer] = currentStreaming
      // Final `_activities` snapshots from a just-finished turn must not replace
      // the next turn's activity list (queue auto-drain race).
      if (!streamOwnsStreamingSlot(streamContext.currentResponse.turn_id, currentAnswer.turn_id)) {
        return
      }

      const mergedActivities = mergeActivitiesPreservingClientInjected(
        activities,
        currentAnswer.activities
      )

      const mergedResponse: ChatAppResponse = {
        ...currentAnswer,
        activities: mergedActivities,
      }

      // Keep the in-progress response in sync so a later content chunk's
      // activity-merge sees the latest snapshot rather than a stale one.
      streamContext.currentResponse = {
        ...streamContext.currentResponse,
        activities: mergedActivities,
      }

      scheduleContentUpdate(
        streamContext.writeChatState,
        {
          streamingAnswer: [existingQuestion ?? streamContext.question, mergedResponse],
        },
        streamContext
      )
    },
    [scheduleContentUpdate]
  )

  const processStreamContent = useCallback(
    (streamContext: StreamContext, content: string) => {
      const { streamedContent, currentResponse } = streamContext
      const updatedContent = streamedContent + content

      scheduleStateUpdate(streamContext.writeChatState, { isLoading: false }, streamContext)

      // Preserve activities that may have been added by WebSocket handler or _activities events
      // Read from the live store, not a stale snapshot
      const chatState = streamContext.getChatState()
      const currentStreaming = chatState.streamingAnswer
      // Check array length, not truthiness - empty array [] is truthy but should be ignored
      const storedActivities = currentStreaming?.[1]?.activities
      const existingActivities =
        (storedActivities && storedActivities.length > 0
          ? storedActivities
          : currentResponse.activities) || []

      const latestResponse: ChatAppResponse = {
        ...currentResponse,
        content: updatedContent,
        activities: existingActivities,
      }

      // Keep the in-progress response in sync so that subsequent context-only
      // events don't overwrite the streamed content with an empty string.
      streamContext.currentResponse = latestResponse

      updateStreamedAnswers(streamContext, latestResponse)

      // Per-chunk logging disabled for performance - only summary logs remain
      return updatedContent
    },
    [scheduleStateUpdate, updateStreamedAnswers]
  )

  const synchronizeToolApprovals = useCallback(
    (streamContext: StreamContext, toolApprovals: ToolApproval[]) => {
      const normalized = toolApprovals.length > 0 ? toolApprovals : []

      // Get current tool approvals from streamingAnswer context
      const chatState = streamContext.getChatState()
      const currentStreaming = chatState.streamingAnswer
      const currentApprovals = currentStreaming?.[1]?.context?.tool_approvals ?? []

      logger.info("Applying tool approval update", {
        sessionId: streamContext.sessionId,
        requestCount: normalized.length,
        requests: normalized.map((approval) => ({
          requestId: approval.request_id,
          status: approval.status,
        })),
      })

      // Check if approvals have actually changed
      if (
        buildToolApprovalSignature(currentApprovals as ToolApproval[]) ===
        buildToolApprovalSignature(normalized)
      ) {
        // No changes, just update state
        scheduleStateUpdate(
          streamContext.writeChatState,
          {
            toolApprovals: normalized,
            toolApprovalSessionId: streamContext.sessionId ?? null,
          },
          streamContext
        )
        return
      }

      // Update streamingAnswer with new tool approvals in context
      if (currentStreaming) {
        const [question, currentAnswer] = currentStreaming
        const updatedAnswer: ChatAppResponse = {
          ...currentAnswer,
          context: {
            ...currentAnswer.context,
            tool_approvals: normalized,
          },
        }

        scheduleStateUpdate(
          streamContext.writeChatState,
          {
            streamingAnswer: [question, updatedAnswer],
            toolApprovals: normalized,
            toolApprovalSessionId: streamContext.sessionId ?? null,
          },
          streamContext
        )
      } else {
        // No streaming answer yet, just update state
        scheduleStateUpdate(
          streamContext.writeChatState,
          {
            toolApprovals: normalized,
            toolApprovalSessionId: streamContext.sessionId ?? null,
          },
          streamContext
        )
      }
    },
    [scheduleStateUpdate]
  )

  const processStreamResponse = useCallback(
    (event: StreamEvent, streamContext: StreamContext) => {
      if (streamContext.signal.aborted) {
        const error = new Error("AbortError")
        error.name = "AbortError"
        throw error
      }

      // Authoritative activities snapshot (also handled in the streamHandler
      // loop before this is called — kept here so unit tests and any direct
      // caller preserve client-injected steer_applied across the replace).
      if (event._activities && Array.isArray(event._activities)) {
        updateStreamedActivities(streamContext, event._activities)
        return streamContext.streamedContent
      }

      // Do NOT early-return for inactive sessions here. Content must keep
      // accumulating into `currentResponse`/the returned string even while the
      // user is viewing another chat (background completion is a supported
      // flow), otherwise chunks that arrive while backgrounded are permanently
      // dropped and the answer renders with a hole once the user switches back.
      // Every scheduled write uses a request-scoped writer bound to the
      // originating session, so background chunks can safely update that
      // session without touching whichever chat the user is currently viewing.
      const { content } = event
      const responseContext = event.context
      const responseComplete = event.response_complete === true
      let contextApplied = false
      let contextKeys: string[] = []

      // Apply context before `response_complete` so a single chunk that includes both
      // merges documents (and other fields) into `currentResponse` before we copy it
      // into `streamingAnswer`.
      // If we found responseContext, update the current response's context.
      // Avoid pushing context-only changes to prevent
      // expensive re-renders during streaming (e.g., follow-up questions).
      if (responseContext) {
        contextApplied = true
        if (
          responseContext &&
          typeof responseContext === "object" &&
          !Array.isArray(responseContext)
        ) {
          contextKeys = Object.keys(responseContext as Record<string, unknown>)
        }
        const defaultContext = {
          data_points: [],
          followup_questions: null,
          thoughts: [],
        }
        const mergedDocumentReferences = mergeDocumentReferences(
          streamContext.currentResponse.context?.document_references,
          (responseContext as { document_references?: unknown }).document_references
        )
        // Capture existing pending_files before the spread merge overwrites them
        const previousPending =
          (streamContext.currentResponse.context as { pending_files?: PendingFile[] })
            ?.pending_files ?? []

        streamContext.currentResponse.context = {
          ...defaultContext,
          ...streamContext.currentResponse.context,
          ...responseContext,
          ...(mergedDocumentReferences ? { document_references: mergedDocumentReferences } : {}),
        }

        // Accumulate pending files (each chunk adds one file, so merge rather than replace)
        const incomingPending = (responseContext as { pending_files?: PendingFile[] }).pending_files
        if (Array.isArray(incomingPending)) {
          const seenIds = new Set(previousPending.map((p: PendingFile) => p.file_id))
          const newFiles = incomingPending.filter((p: PendingFile) => !seenIds.has(p.file_id))
          streamContext.currentResponse.context.pending_files = [...previousPending, ...newFiles]
        } else if (previousPending.length > 0) {
          // Preserve previously accumulated pending files even when this chunk has none
          streamContext.currentResponse.context.pending_files = previousPending
        }

        // Handle tool approvals
        const toolApprovalsRaw = (responseContext as { tool_approvals?: unknown }).tool_approvals
        if (Array.isArray(toolApprovalsRaw)) {
          const toolApprovals = toolApprovalsRaw.filter(
            (item): item is ToolApproval =>
              !!item &&
              typeof item === "object" &&
              "request_id" in item &&
              "status" in item &&
              "call_id" in item
          )

          if (toolApprovals.length > 0) {
            synchronizeToolApprovals(streamContext, toolApprovals)
          }
        }
      }

      if (responseComplete) {
        // Mark the in-flight response as complete so action icons render immediately
        // without waiting for the slower message_saved WebSocket event.
        const chatState = streamContext.getChatState()
        const currentStreaming = chatState.streamingAnswer
        if (currentStreaming) {
          const [q, currentAnswer] = currentStreaming
          // Merge any context updates applied to currentResponse that weren't pushed
          // to state (e.g., assistant_files from code interpreter file uploads).
          const mergedContext = {
            ...currentAnswer.context,
            ...streamContext.currentResponse.context,
          }
          // Promote assistant/user files from context to top-level for fileMeta lookup
          const ctxFiles = mergedContext as Record<string, unknown>
          const ctxAssistantFiles = Array.isArray(ctxFiles.assistant_files)
            ? (ctxFiles.assistant_files as string[])
            : undefined
          const ctxUserFiles = Array.isArray(ctxFiles.user_files)
            ? (ctxFiles.user_files as string[])
            : undefined
          const completedAnswer: ChatAppResponse = {
            ...currentAnswer,
            // `currentAnswer` comes from a live-store read, which can be
            // stale: response_complete is its own WebSocket message and
            // can arrive (and be processed) before the last content
            // chunk's debounced store update has flushed. `currentResponse`
            // is updated synchronously on every chunk (see
            // processStreamContent), so it's always at least as complete.
            content: streamContext.currentResponse.content,
            assistant_files: currentAnswer.assistant_files ?? ctxAssistantFiles,
            user_files: currentAnswer.user_files ?? ctxUserFiles,
            context: mergedContext,
            completed: true,
          }
          scheduleStateUpdate(
            streamContext.writeChatState,
            {
              streamingAnswer: [q, completedAnswer],
              isLoading: false,
              isStreaming: false,
            },
            streamContext
          )
        } else {
          scheduleStateUpdate(
            streamContext.writeChatState,
            {
              isLoading: false,
              isStreaming: false,
            },
            streamContext
          )
        }
      }

      // If we found content, process it
      if (content) {
        return processStreamContent(streamContext, content)
      }

      if (contextApplied) {
        logger.debug("Processed context-only stream chunk", {
          sessionId: streamContext.sessionId,
          contextKeys,
          streamedLength: streamContext.streamedContent.length,
          toolApprovalCount: streamContext.currentResponse.context?.tool_approvals?.length ?? 0,
        })
      }

      return streamContext.streamedContent
    },
    [processStreamContent, scheduleStateUpdate, synchronizeToolApprovals, updateStreamedActivities]
  )

  const initializeStreamResponse = useCallback(
    (
      isContinuation: boolean,
      getChatState: () => {
        messages: [string, ChatAppResponse][]
        streamingAnswer: [string, ChatAppResponse] | null
      }
    ): [ChatAppResponse, string] => {
      const initialResponse: ChatAppResponse = {
        content: "",
        context: { data_points: [], followup_questions: null, thoughts: [] },
        session_state: null,
        completed: false,
      }

      if (!isContinuation) {
        return [initialResponse, ""]
      }

      const chatState = getChatState()
      const lastResponse = chatState.messages[chatState.messages.length - 1]?.[1]
      return [{ ...lastResponse }, lastResponse.content]
    },
    []
  )

  const finalizeStreamResponse = useCallback(
    (streamContext: StreamContext, updatedContent: string) => {
      const { currentResponse, question, getChatState, writeChatState } = streamContext
      const chatState = getChatState()
      const currentStreaming = chatState.streamingAnswer

      if (!currentStreaming) {
        // The WebSocket message_saved handler already finalized this message
        // (moved it to messages and cleared streamingAnswer). Discard this stream's
        // stale pending update (e.g., response_complete scheduling streamingAnswer)
        // without cancelling work owned by another stream.
        const settleKey = clearPendingStateForStream(
          writeChatState,
          streamContext.sessionId,
          streamContext.currentResponse.turn_id
        )
        const existingSettleTimeout = settleTimeoutsRef.current.get(settleKey)
        if (existingSettleTimeout != null) {
          clearTimeout(existingSettleTimeout)
          settleTimeoutsRef.current.delete(settleKey)
        }
        return currentResponse
      }

      const [, streamAnswer] = currentStreaming

      // Create final response with all accumulated data (returned to caller)
      const finalResponse: ChatAppResponse = {
        ...streamAnswer, // Includes activities added during streaming
        content: updatedContent,
        completed: true,
      }

      flushPendingState()

      const settleKey = getPendingKey(
        writeChatState,
        streamContext.sessionId,
        streamContext.currentResponse.turn_id
      )

      // Clear only this stream's previously scheduled settle timeout to prevent
      // duplicate finish events without cancelling another session's completion.
      const existingSettleTimeout = settleTimeoutsRef.current.get(settleKey)
      if (existingSettleTimeout != null) {
        clearTimeout(existingSettleTimeout)
        settleTimeoutsRef.current.delete(settleKey)
      }

      // Allow the last content paint to settle before swapping streaming → completed
      const settleTimeout = setTimeout(() => {
        settleTimeoutsRef.current.delete(settleKey)

        // If message_saved already finalized (streamingAnswer cleared), skip.
        const latestChat = getChatState()
        if (!latestChat.streamingAnswer) return

        // Re-read the latest streamingAnswer — message_saved may have merged
        // backend metadata (messageId, assistant_files, user_files) into it
        // since finalState was captured.
        const [latestQ, latestAnswer] = latestChat.streamingAnswer

        // A newer turn may have replaced streamingAnswer while this settle was
        // pending (fast follow-up send). Ownership is proven by turn id — NOT
        // question text, which an identical follow-up ("continue", "continue")
        // would defeat. When the slot belongs to a newer turn: never adopt its
        // question or clobber its in-flight stream state — finalize only the
        // pair this settle was armed for, unless message_saved already landed
        // this exchange in messages (dedupe by turn id or question+content).
        const ownsStreamingSlot =
          currentResponse.turn_id != null
            ? latestAnswer.turn_id === currentResponse.turn_id
            : latestQ === question
        if (!ownsStreamingSlot) {
          const alreadySaved = latestChat.messages.some(
            ([mq, mr]) =>
              (currentResponse.turn_id != null && mr.turn_id === currentResponse.turn_id) ||
              (mq === question && mr.content === updatedContent)
          )
          if (alreadySaved) return
          const orphanFinalState: Partial<ChatState> = {
            messages: [
              ...latestChat.messages,
              [question, { ...currentResponse, content: updatedContent, completed: true }],
            ],
          }
          writeChatState(orphanFinalState)
          return
        }
        const latestCtx = latestAnswer.context
        const streamCtx = streamContext.currentResponse.context
        const mergedDocumentReferencesForSettle = mergeDocumentReferences(
          latestCtx.document_references,
          streamCtx.document_references
        )
        // Spread stream first, then store (`latestCtx`) so `message_saved` merges inside
        // the settle window are not overwritten by stale `currentResponse`. Prefer the
        // richer `documents` array by length when both sides differ.
        const latestDocCount = latestCtx.documents?.length ?? 0
        const streamDocCount = streamCtx.documents?.length ?? 0
        const mergedDocuments =
          latestDocCount >= streamDocCount ? latestCtx.documents : streamCtx.documents
        const settledContext = {
          ...streamCtx,
          ...latestCtx,
          ...(mergedDocuments !== undefined ? { documents: mergedDocuments } : {}),
          ...(mergedDocumentReferencesForSettle != null &&
          mergedDocumentReferencesForSettle.length > 0
            ? { document_references: mergedDocumentReferencesForSettle }
            : {}),
        }
        const refreshedFinalState: Partial<ChatState> = {
          messages: [
            ...latestChat.messages,
            [
              latestQ ?? question,
              {
                // The spread must stay first: a cancelled turn's message_saved
                // merge stamps `interrupted`/`termination_reason` onto the
                // streaming answer (issue #2438), and the settle move has to
                // carry them into the terminal message (completed stays true).
                ...latestAnswer,
                content: updatedContent,
                completed: true,
                context: settledContext,
              },
            ],
          ],
          // Do NOT clear isAwaitingSave here: it stays true until the backend's
          // message_saved event arrives, so the user can't send the next turn
          // before the previous save is routed. The Branch B handler in
          // use-websocket-message-handler then clears the flag on arrival.
          // Clearing it here would let an identical follow-up question be
          // misrouted by the question-text match in Branch A. See PR #1810.
          streamingAnswer: null,
          canContinue: false,
          isStreaming: false,
          isLoading: false,
          toolApprovals: [],
          toolApprovalSessionId: null,
        }

        writeChatState(refreshedFinalState)
      }, FINALIZE_SETTLE_MS)
      settleTimeoutsRef.current.set(settleKey, settleTimeout)

      return finalResponse
    },
    [clearPendingStateForStream, flushPendingState, getPendingKey]
  )

  const handleStreamError = useCallback(
    (streamContext: StreamContext) => {
      const { streamedContent, currentResponse, question, getChatState } = streamContext

      logger.warn(
        `streamHandler: handling stream error for session ${streamContext.sessionId ?? "null"}`
      )

      flushPendingState()

      // A terminal save or a fast follow-up may have claimed the streaming
      // slot before the error reaches this handler. Never revive the retired
      // turn or stop the newer one with this stream's terminal error state.
      const currentStreaming = getChatState().streamingAnswer
      if (
        !currentStreaming ||
        !streamOwnsStreamingSlot(currentResponse.turn_id, currentStreaming[1].turn_id)
      ) {
        return
      }

      // If the request was aborted by the user, mark as completed so the spinner stops.
      // Otherwise keep as incomplete to allow continuation.
      const isAborted = streamContext.signal.aborted
      const [liveQuestion, liveResponse] = currentStreaming
      const incompleteResponse = {
        ...liveResponse,
        content: streamedContent,
        completed: isAborted,
      }

      streamContext.writeChatState({
        streamingAnswer: [liveQuestion ?? question, incompleteResponse],
        isStreaming: false,
        isLoading: false,
        isAwaitingSave: false,
        toolApprovals: [],
        toolApprovalSessionId: null,
      })
    },
    [flushPendingState]
  )

  const handleStreamRequest = useCallback(
    async ({
      question,
      responseBody,
      signal,
      isContinuation = false,
      writeChatState,
      initialResponse,
      sessionId,
      getChatState,
    }: StreamRequestOptions) => {
      const [currentResponse, initialContent] = initialResponse
        ? [initialResponse, initialResponse.content]
        : initializeStreamResponse(isContinuation, getChatState)

      // Every stream request is one turn. The id travels with the streaming
      // answer (message_saved merges preserve it via spread) so the settle
      // timeout can prove ownership even when a fast follow-up repeats the
      // exact same question text.
      currentResponse.turn_id =
        globalThis.crypto?.randomUUID?.() ?? `turn-${Date.now()}-${Math.random()}`
      // Wall-clock mark so late WS activity frames from the previous turn
      // (queue drain / fast follow-up) can be dropped — those frames have
      // older `timestamp` values and must not merge into this turn's bubble.
      currentResponse.stream_started_at = Date.now()

      const streamContext: StreamContext = {
        streamedContent: initialContent,
        question,
        currentResponse,
        signal,
        isContinuation,
        sessionId,
        writeChatState,
        getChatState,
      }

      // Guards against applying the error state twice: the in-stream error
      // branch below calls handleStreamError and then throws, which would
      // otherwise re-run handleStreamError in the catch block — re-applying
      // state on top of whatever the first call already settled.
      let streamErrorHandled = false
      try {
        // Initialize streamingAnswer ONCE at stream start
        flushPendingState()
        const initialStreamingAnswer: [string, ChatAppResponse] = [question, currentResponse]

        writeChatState({
          streamingAnswer: initialStreamingAnswer,
          isStreaming: true,
        })

        const { default: readNDJSONStream } = await safeImport(
          () => import("ndjson-readablestream")
        )
        let accumulatedContent = initialContent

        // Process the stream for content
        for await (const event of readNDJSONStream(responseBody)) {
          if (!event) continue

          // Check for error events
          if (event?.error || event?.type === "error") {
            // Deep Explorer (and other agent runtimes) emit error frames as
            // {type: "error", data: {message, source, retry_after_hint?}}.
            // Plain chat emits a flatter {error: "..."} or {message: "..."}.
            // Read both shapes so real failures (RequestEntityTooLarge, 429
            // exhaustion, etc.) surface in the UI instead of "Unknown error".
            const errorMessage = String(
              event?.data?.message || event?.message || event?.error || "Unknown error"
            )
            handleStreamError(streamContext)
            streamErrorHandled = true

            // Rethrow to bubble up to the UI
            throw new Error(errorMessage)
          }

          // Look for activities in special hidden event format. Push them onto
          // the streaming answer and render immediately so live reasoning
          // summaries (and other activities) appear during the pre-first-token
          // wait — not only once content starts.
          if (event._activities && Array.isArray(event._activities)) {
            updateStreamedActivities(streamContext, event._activities)
            continue // Skip processing this special event
          }

          streamContext.streamedContent = accumulatedContent
          accumulatedContent = await processStreamResponse(event, streamContext)
        }
        const finalized = finalizeStreamResponse(streamContext, accumulatedContent)
        return finalized
      } catch (e) {
        logger.error(
          `streamHandler: error during stream for session ${sessionId ?? "null"}: ${(e as Error).message}`
        )
        if (!streamErrorHandled) {
          handleStreamError(streamContext)
        }
        throw e
      }
    },
    [
      processStreamResponse,
      initializeStreamResponse,
      finalizeStreamResponse,
      handleStreamError,
      flushPendingState,
      updateStreamedActivities,
    ]
  )

  return {
    processStreamResponse,
    initializeStreamResponse,
    finalizeStreamResponse,
    handleStreamError,
    handleStreamRequest,
  }
}
